// src/utils/reversePathD.js
// Reverses an SVG path's `d` string exactly — same geometry, opposite
// direction of travel. Supports the full drawing-command set exporters
// (Illustrator, Figma, etc.) actually emit: M, L, H, V, C, S, Q, T, A, Z —
// absolute + relative, upper/lower case.

function createReader(d) {
  let i = 0
  const len = d.length

  function skipSeparators() {
    while (i < len && /[\s,]/.test(d[i])) i++
  }

  function readNumber() {
    skipSeparators()
    let j = i
    if (d[j] === "-" || d[j] === "+") j++
    while (j < len && /[0-9]/.test(d[j])) j++
    if (d[j] === ".") {
      j++
      while (j < len && /[0-9]/.test(d[j])) j++
    }
    if (d[j] === "e" || d[j] === "E") {
      j++
      if (d[j] === "-" || d[j] === "+") j++
      while (j < len && /[0-9]/.test(d[j])) j++
    }
    const num = parseFloat(d.slice(i, j))
    i = j
    return num
  }

  // Arc flags are always a single 0/1 digit and are often written with no
  // separator before the next number (e.g. "...0,1 50,50" or even
  // "...0150,50"), so they're read one character at a time — a normal
  // number scan would swallow the next value's digits too.
  function readFlag() {
    skipSeparators()
    const ch = d[i]
    i++
    return ch === "1" ? 1 : 0
  }

  function peekCommand() {
    skipSeparators()
    return i < len && /[MLHVCSQTAZmlhvcsqtaz]/.test(d[i]) ? d[i] : null
  }

  function readCommand() {
    skipSeparators()
    return d[i++]
  }

  function hasMore() {
    skipSeparators()
    return i < len
  }

  return { readNumber, readFlag, peekCommand, readCommand, hasMore }
}

function quadToCubic(cur, cq, to) {
  const c1 = { x: cur.x + (2 / 3) * (cq.x - cur.x), y: cur.y + (2 / 3) * (cq.y - cur.y) }
  const c2 = { x: to.x + (2 / 3) * (cq.x - to.x), y: to.y + (2 / 3) * (cq.y - to.y) }
  return { type: "C", from: cur, c1, c2, to }
}

export function parsePathToSegments(d) {
  const r = createReader(d)
  let cur = { x: 0, y: 0 }
  let start = { x: 0, y: 0 }
  let prevControl = null // last cubic/quad control point, for S/T reflection
  let prevType = null
  let cmd = null
  const segments = []

  while (r.hasMore()) {
    const next = r.peekCommand()
    if (next) cmd = r.readCommand()
    // else: implicit repeat of the previous command — keep cmd as-is

    switch (cmd) {
      case "M":
        cur = { x: r.readNumber(), y: r.readNumber() }
        start = { ...cur }
        cmd = "L" // subsequent implicit pairs are linetos, per spec
        prevControl = null
        break
      case "m":
        cur = { x: cur.x + r.readNumber(), y: cur.y + r.readNumber() }
        start = { ...cur }
        cmd = "l"
        prevControl = null
        break
      case "L": {
        const to = { x: r.readNumber(), y: r.readNumber() }
        segments.push({ type: "L", from: cur, to })
        cur = to
        prevControl = null
        break
      }
      case "l": {
        const to = { x: cur.x + r.readNumber(), y: cur.y + r.readNumber() }
        segments.push({ type: "L", from: cur, to })
        cur = to
        prevControl = null
        break
      }
      case "H": {
        const to = { x: r.readNumber(), y: cur.y }
        segments.push({ type: "L", from: cur, to })
        cur = to
        prevControl = null
        break
      }
      case "h": {
        const to = { x: cur.x + r.readNumber(), y: cur.y }
        segments.push({ type: "L", from: cur, to })
        cur = to
        prevControl = null
        break
      }
      case "V": {
        const to = { x: cur.x, y: r.readNumber() }
        segments.push({ type: "L", from: cur, to })
        cur = to
        prevControl = null
        break
      }
      case "v": {
        const to = { x: cur.x, y: cur.y + r.readNumber() }
        segments.push({ type: "L", from: cur, to })
        cur = to
        prevControl = null
        break
      }
      case "C": {
        const c1 = { x: r.readNumber(), y: r.readNumber() }
        const c2 = { x: r.readNumber(), y: r.readNumber() }
        const to = { x: r.readNumber(), y: r.readNumber() }
        segments.push({ type: "C", from: cur, c1, c2, to })
        cur = to
        prevControl = c2
        break
      }
      case "c": {
        const c1 = { x: cur.x + r.readNumber(), y: cur.y + r.readNumber() }
        const c2 = { x: cur.x + r.readNumber(), y: cur.y + r.readNumber() }
        const to = { x: cur.x + r.readNumber(), y: cur.y + r.readNumber() }
        segments.push({ type: "C", from: cur, c1, c2, to })
        cur = to
        prevControl = c2
        break
      }
      case "S": {
        const c1 =
          prevType === "C" || prevType === "S"
            ? { x: 2 * cur.x - prevControl.x, y: 2 * cur.y - prevControl.y }
            : { ...cur }
        const c2 = { x: r.readNumber(), y: r.readNumber() }
        const to = { x: r.readNumber(), y: r.readNumber() }
        segments.push({ type: "C", from: cur, c1, c2, to })
        cur = to
        prevControl = c2
        break
      }
      case "s": {
        const c1 =
          prevType === "C" || prevType === "S"
            ? { x: 2 * cur.x - prevControl.x, y: 2 * cur.y - prevControl.y }
            : { ...cur }
        const c2 = { x: cur.x + r.readNumber(), y: cur.y + r.readNumber() }
        const to = { x: cur.x + r.readNumber(), y: cur.y + r.readNumber() }
        segments.push({ type: "C", from: cur, c1, c2, to })
        cur = to
        prevControl = c2
        break
      }
      case "Q": {
        const cq = { x: r.readNumber(), y: r.readNumber() }
        const to = { x: r.readNumber(), y: r.readNumber() }
        segments.push(quadToCubic(cur, cq, to))
        cur = to
        prevControl = cq
        break
      }
      case "q": {
        const cq = { x: cur.x + r.readNumber(), y: cur.y + r.readNumber() }
        const to = { x: cur.x + r.readNumber(), y: cur.y + r.readNumber() }
        segments.push(quadToCubic(cur, cq, to))
        cur = to
        prevControl = cq
        break
      }
      case "T": {
        const cq =
          prevType === "Q" || prevType === "T"
            ? { x: 2 * cur.x - prevControl.x, y: 2 * cur.y - prevControl.y }
            : { ...cur }
        const to = { x: r.readNumber(), y: r.readNumber() }
        segments.push(quadToCubic(cur, cq, to))
        cur = to
        prevControl = cq
        break
      }
      case "t": {
        const cq =
          prevType === "Q" || prevType === "T"
            ? { x: 2 * cur.x - prevControl.x, y: 2 * cur.y - prevControl.y }
            : { ...cur }
        const to = { x: cur.x + r.readNumber(), y: cur.y + r.readNumber() }
        segments.push(quadToCubic(cur, cq, to))
        cur = to
        prevControl = cq
        break
      }
      case "A":
      case "a": {
        const rx = r.readNumber()
        const ry = r.readNumber()
        const xRot = r.readNumber()
        const largeArc = r.readFlag()
        const sweep = r.readFlag()
        const to =
          cmd === "A"
            ? { x: r.readNumber(), y: r.readNumber() }
            : { x: cur.x + r.readNumber(), y: cur.y + r.readNumber() }
        segments.push({ type: "A", from: cur, to, rx, ry, xRot, largeArc, sweep })
        cur = to
        prevControl = null
        break
      }
      case "Z":
      case "z":
        if (cur.x !== start.x || cur.y !== start.y) {
          segments.push({ type: "L", from: cur, to: start })
        }
        cur = { ...start }
        prevControl = null
        break
      default:
        // Unrecognized command — stop rather than silently misreading
        // the remaining coordinates under the wrong command.
        return segments
    }
    prevType = cmd.toUpperCase()
  }
  return segments
}

export function reversePathD(d) {
  const segments = parsePathToSegments(d)
  if (!segments.length) return d
  const reversed = [...segments].reverse()
  const startPoint = reversed[0].to
  let out = `M ${startPoint.x} ${startPoint.y} `
  out += reversed
    .map(seg => {
      if (seg.type === "C") {
        return `C ${seg.c2.x} ${seg.c2.y} ${seg.c1.x} ${seg.c1.y} ${seg.from.x} ${seg.from.y}`
      }
      if (seg.type === "A") {
        // Same arc, endpoints swapped, sweep direction flipped.
        return `A ${seg.rx} ${seg.ry} ${seg.xRot} ${seg.largeArc} ${seg.sweep ? 0 : 1} ${seg.from.x} ${seg.from.y}`
      }
      return `L ${seg.from.x} ${seg.from.y}`
    })
    .join(" ")
  return out
}